import React from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ProductRow from './ProductRow';
import { resolveSort } from '../../../utils/buyerInsightsFormat';
import { columnHeaderCells, columnsFor, groupHeaderCells } from './listColumns';

const ListSkeleton = ({ isWide }) => (
  <Box>
    {[0, 1, 2, 3].map((i) => (
      <Skeleton key={i} variant="rectangular" height={isWide ? 56 : 120} sx={{ borderRadius: 1, mb: 1 }} />
    ))}
  </Box>
);

// The product list (spec §2.3 / §2.6): wide = table with a two-level sortable header inside its
// own horizontal scroll; narrow = a stack of cards. Rows render ProductRow.
const ProductList = ({
  rows,
  side,
  view,
  isWide,
  loading,
  loadingMore,
  hasMore,
  onShowMore,
  sort,
  onSort,
  isExpanded,
  onToggleRow,
  expandDisabled,
  window,
  n,
  myRetailerId,
  filters,
  emptyState,
}) => {
  const activeSort = resolveSort(sort, side);
  const columns = columnsFor(side, activeSort, { revenue: view !== 'only_you' });

  if (loading && (!rows || rows.length === 0)) return <ListSkeleton isWide={isWide} />;
  if (!loading && rows.length === 0) return emptyState;

  const rowProps = (row) => ({
    row,
    view,
    side,
    isWide,
    expanded: isExpanded(row.product_id),
    onToggle: onToggleRow,
    expandDisabled,
    columns,
    window,
    n,
    myRetailerId,
    filters,
  });

  const showMore = hasMore && (
    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <Button variant="outlined" onClick={onShowMore} disabled={loadingMore} sx={{ minHeight: isWide ? 36 : 44 }}>
        {loadingMore ? 'Loading…' : 'Show more'}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      {isWide ? (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Products">
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ width: 40, px: 0.5 }} />
                <TableCell rowSpan={2}>Product</TableCell>
                {groupHeaderCells(columns)}
              </TableRow>
              <TableRow>
                {columnHeaderCells(columns, onSort)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <ProductRow key={row.product_id} {...rowProps(row)} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
          {rows.map((row) => (
            <ProductRow key={row.product_id} {...rowProps(row)} />
          ))}
        </Box>
      )}
      {showMore}
    </Box>
  );
};

export default ProductList;
